import React, { useEffect, useState } from "react";
import { listDocuments, deleteDocument, getVersions } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import { FileText, Trash2, History, Clock } from "lucide-react";
import { toast } from "sonner";

const LibraryPanel = ({ refreshKey, onLoad }) => {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [versions, setVersions] = useState([]);
  const [versionDoc, setVersionDoc] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      setDocs(await listDocuments());
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [refreshKey]);

  const handleDelete = async (id, e) => {
    e.stopPropagation();
    await deleteDocument(id);
    toast.success("Document deleted");
    load();
  };

  const openVersions = async (doc, e) => {
    e.stopPropagation();
    setVersionDoc(doc);
    setVersions(await getVersions(doc.id));
  };

  if (loading) return <div className="text-sm text-slate-400 py-8 text-center">Loading library…</div>;
  if (docs.length === 0)
    return (
      <div className="text-sm text-slate-400 py-12 text-center border border-dashed border-slate-200 rounded-md">
        No saved documents yet. Generate and save a document to build your library.
      </div>
    );

  return (
    <div className="space-y-2" data-testid="library-panel">
      {docs.map((d) => (
        <div
          key={d.id}
          onClick={() => onLoad(d)}
          className="group border border-slate-200 rounded-md p-3 hover:border-[#002FA7] hover:shadow-sm cursor-pointer transition-all bg-white"
          data-testid={`library-item-${d.id}`}
        >
          <div className="flex items-start justify-between gap-2">
            <div className="flex items-start gap-2.5 min-w-0">
              <FileText size={16} className="text-[#002FA7] mt-0.5 shrink-0" />
              <div className="min-w-0">
                <div className="font-semibold text-sm text-slate-800 truncate">{d.title}</div>
                <div className="text-[11px] text-slate-500 mt-0.5">
                  {d.doc_type_label} · {d.metadata?.document_number} · v{d.current_version}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-1.5 opacity-0 group-hover:opacity-100 transition-opacity">
              <button onClick={(e) => openVersions(d, e)} className="text-slate-400 hover:text-[#002FA7]" data-testid={`library-versions-${d.id}`}>
                <History size={15} />
              </button>
              <button onClick={(e) => handleDelete(d.id, e)} className="text-slate-400 hover:text-red-600" data-testid={`library-delete-${d.id}`}>
                <Trash2 size={15} />
              </button>
            </div>
          </div>
        </div>
      ))}

      <Dialog open={!!versionDoc} onOpenChange={(o) => !o && setVersionDoc(null)}>
        <DialogContent data-testid="versions-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Version History — {versionDoc?.title}</DialogTitle>
          </DialogHeader>
          <div className="space-y-2 max-h-[400px] overflow-y-auto">
            {versions.map((v) => (
              <div key={v.id} className="flex items-center justify-between border border-slate-200 rounded-md p-3">
                <div className="flex items-center gap-2">
                  <Clock size={14} className="text-slate-400" />
                  <span className="font-semibold text-sm">Version {v.version_num}</span>
                </div>
                <span className="text-xs text-slate-500">
                  {new Date(v.created_at).toLocaleString()}
                </span>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default LibraryPanel;
